import { Box, Divider, Grid, Typography } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import ChartWidget from 'components/Widgets/ChartWidget';
import CustomBarChartWidget from 'components/Widgets/CustomBarChart';
import NumberWidget from 'components/Widgets/NumberWidget';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { addOrUpdateInsights, deleteInsights, fetchHappySignalOverview, getInsights, fetchHappySignalCapabilityList, fetchHappySignalTechnologyList, fetchHappySignalOverviewTrend, getDataEstateSector } from 'service/service';
import Insights from './Insights';
import LoaderComponent from 'components/Loader';
import IncidentFilter from 'components/Dropdown/IncidentFilter';
import SectorFilter from 'components/Dropdown/SectorFilter';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import YearMonthPicker from 'components/Dropdown/YearMonthPicker';
import { SECTOR_OPTIONS } from 'utils/xopsUtil';

function HappySignalOverview() {
    const theme = useTheme();
    const [sectorName, setSectorName] = useState(() => {
        const storedValue = localStorage.getItem("xopsHappySignalOverviewSector");
        return storedValue === null || storedValue === undefined ? "all" : storedValue;
    });
    const [isLoading, setIsLoading] = React.useState(false);
    const [isTrendLoading, setIsTrendLoading] = React.useState(false);
    const [responseVolumeData, setResponseVolumeData] = useState<any>(null);
    const [responseVolumeBarChartOptions, setResponseVolumeBarChartOptions] = useState<any>({});
    const [happinessIndexTrendData, setHappinessIndexTrendData] = useState<{ [key: string]: number } | null>({});
    const [widgetsData, setWidgetsData] = useState({
        happinessIndex: { value: 0, change: '-' },
        averageLostTime: { value: '', change: '-' },
        responseRate: { value: '', change: '-' },
        uniquePersonas: { value: 0, change: '-' }
    });
    const [lastYearAvgData, setLastYearAvgData] = useState<[string, number][]>([]);

    const isXOps = true;
    const getInitialMonth = () => {
        return isXOps ? moment().subtract(1, 'month').format('YYYY-MM') : '0M';
    };
    const [loader, setLoader] = useState<boolean>(false);
    const [insights, setInsights] = useState<Insights[]>([]);

    const [customMonth, setCustomMonth] = useState(getInitialMonth());
    const [filterMonth, setFilterMonth] = useState(getInitialMonth());
    const [isYearFilterApplied, setIsYearFilterApplied] = useState(false)
    const initialMonth = getInitialMonth();

    const [technology, setTechnology] = useState('all');
    const [technologyList, setTechnologyList] = useState<any[]>([]);

    const [platform, setPlatform] = useState('all');
    const [platformList, setPlatformList] = useState<any[]>([]);
    const [sectorOptionsList] = useState<any[]>(SECTOR_OPTIONS);

    useEffect(() => {
        fetchAllCharts(filterMonth, platform, technology, sectorName);
        fetchInsights(filterMonth);
        fetchTrendCharts(platform, technology, sectorName, filterMonth);
        fetchPlatformList();
    }, []);

    async function fetchAllCharts(selectedMonth: string, platform: string, technology: string, sector: string) {
        try {
            setIsLoading(true);
            const response = await fetchHappySignalOverview(selectedMonth, platform, technology, sector);

            if (response.success && response.data) {
                const { happinessIndex, averageLostTime, responseRate, uniquePersonas } =
                    response.data;

                // Normalize number widgets
                setWidgetsData({
                    happinessIndex,
                    averageLostTime,
                    responseRate,
                    uniquePersonas
                });
            }
        } catch (error) {
            console.error('Error while fetching chart data:', error);
        } finally {
            setIsLoading(false);
        }
    }

    async function fetchTrendCharts(platform: string, technology: string, sector: string, filterMonth: any) {
        try {
            setIsTrendLoading(true);
            const response = await fetchHappySignalOverviewTrend(platform, technology, sector, '', '', '', filterMonth);

            if (response.success && response.data) {
                const { happinessIndexTrend, responseVolume, happinessIndexLastYearAvg, responseVolumeLastYearAvg } = response.data;

                // Normalize Happiness Index Trend data
                const trendData: { [key: string]: number } = {};
                happinessIndexTrend.forEach((item: any) => {
                    const formattedMonth = moment(item.month, 'MMM YYYY').format('YYYY-MM');
                    trendData[formattedMonth] = item.value;
                });

                setHappinessIndexTrendData(trendData);
                const avgPreviousYearTrendData: [string, number][] = Object.keys(trendData).map(
                    (month) => [month, happinessIndexLastYearAvg] as [string, number]
                );
                setLastYearAvgData(avgPreviousYearTrendData);


                // Normalize Response Volume Bar Chart
                const formattedResponseVolume = responseVolume.map((item: any) => ({
                    name: item.month,
                    value: item.value
                }));
                setResponseVolumeData(formattedResponseVolume);

                const xAxisLabels = formattedResponseVolume.map((item: any) => item.name);
                const seriesData = formattedResponseVolume.map((item: any) => item.value);

                const responseLastYearAvg = seriesData.map(() => responseVolumeLastYearAvg);
                const allValues = [...seriesData, ...responseLastYearAvg];
                const maxVal = Math.max(...allValues);
                const minVal = Math.min(...allValues);

                const yMax = Math.ceil(maxVal / 10) * 10;
                const yMin = Math.max(0, Math.floor(minVal / 10) * 10);

                const BarChartoptions = {
                    title: { show: false },
                    tooltip: {
                        trigger: 'axis',
                        textStyle: { color: '#A7ADCB' },
                        confine: true,
                        backgroundColor: '#003771',
                        formatter: function (params: any[]) {
                            const date = params[0]?.axisValueLabel;
                            let tooltipContent = `<div><strong>Date:</strong> ${date}</div>`;
                            params.forEach((item) => {
                                tooltipContent += `<div><strong>${item.seriesName}</strong>: ${item.value}</div>`;
                            });
                            return tooltipContent;
                        }
                    },
                    grid: { left: '5%', right: '5%', bottom: '10%', containLabel: true },
                    xAxis: {
                        type: 'category',
                        data: xAxisLabels,
                        axisTick: { show: false },
                        axisLine: { show: true },
                        axisLabel: {
                            color: `${theme.palette.text.subtle}`,
                            rotate: window.innerWidth < 600 ? 30 : 0,
                            formatter: (val: string) => (val.length >= 15 ? val.slice(0, 15) + '…' : val)
                        }
                    },
                    yAxis: {
                        type: 'value',
                        min: yMin,
                        max: yMax,
                        splitLine: { show: false },
                        axisLabel: { color: `${theme.palette.text.subtle}` }
                    },
                    series: [
                        {
                            name: 'Response Volume',
                            type: 'bar',
                            data: seriesData,
                            barWidth: '30%',
                            barMaxWidth: '30%',
                            label: { show: true, position: 'top', fontSize: 12, color: '#F0F0F0' },
                            // barCategoryGap: '20%',
                            itemStyle: { color: theme.palette.secondary.main }
                        },
                        {
                            name: `${Number(filterMonth.split('-')?.[0]) - 1} Average`,
                            label: { show: true, position: 'top', fontSize: 12, color: '#00F0F0' },
                            type: 'line',
                            data: responseLastYearAvg,
                            symbol: 'none',
                            color: '#FFC239',
                            lineStyle: { width: 2 }
                        }
                    ],
                    legend: {
                        orient: 'horizontal',
                        bottom: '1%',
                        left: '2%',
                        textStyle: { color: '#A7ADCB' },
                        selectedMode: false,
                        itemWidth: 10,
                        itemHeight: 10,
                        icon: 'roundRect'
                    }
                };

                setResponseVolumeBarChartOptions(BarChartoptions);
            }
        } catch (error) {
            console.error('Error while fetching trend chart data:', error);
        } finally {
            setIsTrendLoading(false);
        }
    }

    // Function to handle adding a new insight
    const handleAddInsight = async (newInsight: any) => {
        try {
            setLoader(true);
            const response = await addOrUpdateInsights({
                title: newInsight?.title,
                xopsType: 'Happy Signals',
                description: newInsight?.description,
                month: filterMonth
            });
            if (response.success) {
                fetchInsights(filterMonth);
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error('Failed to add insight:', error);
        }
    };
    // Function to handle updating an insight
    const handleUpdateInsight = async (updatedInsight: any) => {
        try {
            setLoader(true);
            const response = await addOrUpdateInsights({
                title: updatedInsight?.title,
                xopsType: 'Happy Signals',
                description: updatedInsight?.description,
                insight_id: updatedInsight?.id,
                month: filterMonth
            });
            if (response.success) {
                fetchInsights(filterMonth);
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error('Failed to update insight:', error);
        }
    };
    // Function to handle deleting an insight
    const handleDeleteInsight = async (insightId: number) => {
        try {
            setLoader(true);
            const response = await deleteInsights(insightId);
            if (response.success) {
                fetchInsights(filterMonth);
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error('Failed to delete insight:', error);
        }
    };

    const handleDateChange = (selectedDate: string) => {
        fetchAllCharts(selectedDate, platform, technology, sectorName);
        fetchInsights(selectedDate);
        fetchTrendCharts(platform, technology, sectorName, selectedDate)
    };

    const handleTechnologyChange = (selectedTechnology: string) => {
        fetchAllCharts(filterMonth, platform, selectedTechnology, sectorName);
        fetchTrendCharts(platform, selectedTechnology, sectorName, filterMonth);
    };

    const handlePlatformChange = (selectedPlatform: string) => {
        setPlatform(selectedPlatform);
        setTechnology('all'); // reset technology filter
        fetchAllCharts(filterMonth, selectedPlatform, 'all', sectorName);
        fetchTrendCharts(selectedPlatform, 'all', sectorName, filterMonth);
        fetchTechnologyList(selectedPlatform); // filter technology list by platform
    };

    //Function to fetch insights
    const fetchInsights = async (selectedMonth: string) => {
        setLoader(true);
        try {
            const response = await getInsights('Happy Signals', '', selectedMonth);
            if (response.success) {
                setInsights(response.data.data);
                setLoader(false);
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error('Failed to get insights:', error);
        }
    };

    const fetchPlatformList = async () => {
        try {
            setIsLoading(true);
            const res = await fetchHappySignalCapabilityList();

            if (res.success) {
                const result = res.data?.filter((item: any) => !!item) || []
                setPlatformList(result);
            }
        } catch (error) {
            console.error('Error fetching platform data:', error);
        }
    };

    const fetchTechnologyList = async (selectedPlatform: string) => {
        try {
            setIsLoading(true);
            const res = await fetchHappySignalTechnologyList(selectedPlatform); // pass platform to API

            if (res.success) {
                const result = res.data?.filter((item: any) => !!item) || []
                setTechnologyList(result);
            }
        } catch (error) {
            console.error('Error fetching technology data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const generateTitleSuffixWithMonth = () => {
        const parts: string[] = [];

        if (platform !== 'all') {
            parts.push(`for ${platform}`);
        }
        if (technology !== 'all') {
            parts.push(`(${technology})`);
        }
        if (sectorName !== 'all') {
            parts.push(`in ${sectorName}`);
        }

        // Format the month to be readable, e.g., "April 2025"
        if (filterMonth !== initialMonth) {
            const isYearFilter = filterMonth?.split('-')?.filter(df => !!df)?.length === 1
            const formattedMonth = isYearFilter ? filterMonth : moment(filterMonth, 'YYYY-MM').format('MMMM YYYY');
            parts.push(`- ${formattedMonth}`);
        }

        // Join all parts with a space
        return parts.join(' ');
    };

    const generateTitleSuffixWithoutMonth = () => {
        const parts: string[] = [];

        if (platform !== 'all') {
            parts.push(`for ${platform}`);
        }
        if (technology !== 'all') {
            parts.push(`(${technology})`);
        }
        if (sectorName !== 'all') {
            parts.push(`in ${sectorName}`);
        }

        // Join all parts with a space
        return parts.join(' ');
    };

    const handleSectorChange = (value: string) => {
        setSectorName(value);
        localStorage.setItem('xopsHappySignalOverviewSector', value)
        fetchAllCharts(filterMonth, platform, technology, value);
        fetchTrendCharts(platform, technology, value, filterMonth);
    };

    const dynamicSuffixWithMonth = generateTitleSuffixWithMonth();
    const dynamicSuffixWithoutMonth = generateTitleSuffixWithoutMonth();

    // --- CONSTANT: Styles for dropdowns ---
    // Applied the dark blue background directly to the input elements
    const dropDownSx = {
        minWidth: '160px',
        backgroundColor: '#003771', // Dark Blue Background to match screenshot
        border: '1px solid #4A5568',
        borderRadius: '6px',
        color: 'white',
        '& .MuiSelect-select': { padding: '8px 12px' },
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        '& .MuiSvgIcon-root': { color: 'white' },
        '&.Mui-disabled': {
            backgroundColor: 'rgba(74, 85, 104, 0.2)',
            border: '1px solid rgba(74, 85, 104, 0.5)',
            color: 'grey'
        }
    };
    useEffect(() => {
        setIsYearFilterApplied(filterMonth?.split('-')?.filter(df => !!df)?.length === 1)
    }, [filterMonth])
    return (
        <Box
            sx={() => ({
                p: { xs: 1, md: 1, lg: 1 },
                mt: 5
            })}
        >
            {/* --- MODIFICATION: Filter Section (Right Aligned, No Container) --- */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2, px: { xs: 1, md: 2, lg: 3 } }}>
                {/* 1. Platform */}
                <Box sx={{ minWidth: '180px' }}>
                    <IncidentFilter
                        sectorName={platform}
                        setSectorName={setPlatform}
                        sectorOptionsList={platformList}
                        handleChange={handlePlatformChange}
                        type={'platform'}
                        disabled={false}
                        boxProps={{ sx: dropDownSx }}
                    />
                </Box>

                {/* 2. Technology */}
                <Box sx={{ minWidth: '180px' }}>
                    <IncidentFilter
                        sectorName={technology}
                        setSectorName={setTechnology}
                        sectorOptionsList={technologyList}
                        handleChange={handleTechnologyChange}
                        type={'technology'}
                        disabled={platform === 'all'}
                        boxProps={{ sx: dropDownSx }}
                    />
                </Box>

                {/* 3. Sector */}
                <Box sx={{ minWidth: '180px' }}>
                    <SectorFilter
                        sectorName={sectorName}
                        setSectorName={setSectorName}
                        sectorOptionsList={sectorOptionsList}
                        handleChange={handleSectorChange}
                        hasNotProvided={true}
                        boxProps={{ sx: dropDownSx }}
                    />
                </Box>

                {/* 4. Date */}
                <Box sx={{ minWidth: '180px' }}>
                    <YearMonthPicker
                        customMonth={customMonth}
                        filterMonth={filterMonth}
                        setCustomMonth={setCustomMonth}
                        setFilterMonth={setFilterMonth}
                        handleChange={handleDateChange}
                    />
                    {/* <DateFilterDropdown
                        customMonth={customMonth}
                        filterMonth={filterMonth}
                        setCustomMonth={setCustomMonth}
                        setFilterMonth={setFilterMonth}
                        handleChange={handleDateChange}
                        isXOps={true}
                        boxProps={{ sx: dropDownSx }}
                    /> */}
                </Box>
            </Box>

            <Grid container spacing={2} mt={2}>
                {/* Happiness Index Widget */}
                <Grid item xs={12} md={6} id="happiness-index">
                    <NumberWidget
                        isAnnualMetrics={isYearFilterApplied}
                        title={<HighlightedText text={`Happiness Index ${dynamicSuffixWithMonth}`} />}
                        number={widgetsData.happinessIndex.value < 0 ? widgetsData.happinessIndex.value : '+' + widgetsData.happinessIndex.value}
                        change={parseFloat(widgetsData.happinessIndex.change)}
                        changeType={widgetsData.happinessIndex.change.startsWith('+') ? 'increase' : 'decrease'}
                        tooltipLabel="Overall employee experience score, ranging from –100 to +100."
                        isHappySignalTooltip={true}
                        isLoading={isLoading}
                        footer={{ footerLink: '#', footerLabel: 'View Details' }}
                        showChangePercentage={true}
                    />
                </Grid>

                {/* Average Lost Time Widget */}
                <Grid item xs={12} md={6} id="average-lost-time">
                    <NumberWidget
                        isAnnualMetrics={isYearFilterApplied}
                        title={<HighlightedText text={`Average Lost Time ${dynamicSuffixWithMonth}`} />}
                        number={widgetsData.averageLostTime.value}
                        change={parseFloat(widgetsData.averageLostTime.change)}
                        changeType={widgetsData.averageLostTime.change.startsWith('+') ? 'increase' : 'decrease'}
                        tooltipLabel=""
                        isLoading={isLoading}
                        footer={{ footerLink: '#', footerLabel: 'View Details' }}
                        showChangePercentage={true}
                        reverseTrendColor={true}
                    />
                </Grid>

                <Grid item xs={12} md={6} id="happiness-index-trend">
                    <ChartWidget
                        title={<HighlightedText text={`Happiness Index Trend ${dynamicSuffixWithoutMonth}`} />}
                        data={happinessIndexTrendData}
                        isLoading={isTrendLoading}
                        isFinops={true}
                        isHappySignal={true}
                        lastYearAvgData={lastYearAvgData}
                        tooltipLabel="Overall employee experience score, ranging from –100 to +100."
                        relevantYear={Number(filterMonth.split('-')?.[0])}
                    />
                </Grid>
                <Grid item xs={12} md={6} id="response-volume">
                    <CustomBarChartWidget
                        title={<HighlightedText text={`Response Volume ${dynamicSuffixWithoutMonth}`} />}
                        isLoading={isTrendLoading}
                        data={responseVolumeData}
                        options={responseVolumeBarChartOptions}
                        isHappySignal={true}
                    />
                </Grid>
            </Grid>
            <Box marginTop={4} marginBottom={5} id="insights-actions">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px' }}>
                    <Typography variant="subHeader" sx={{ color: '#009BDB', mr: { xs: "1rem", sm: '2rem' } }}>{<HighlightedText text="Insights/Actions" />}</Typography>
                    <Divider sx={{ flexGrow: 1, backgroundColor: '#009BDB', marginLeft: 2 }} />
                </Box>
                <Typography
                    variant="bodyText"
                    sx={{ fontStyle: 'italic', color: '#71758F' }}
                >
                    You can display and update Insights/Actions if any.
                </Typography>
            </Box>
            {loader ? (
                <LoaderComponent />
            ) : (
                <Box sx={{ mb: 3, px: { xs: 1, md: 2, lg: 3 } }}>
                    <Insights
                        insights={insights}
                        setInsights={setInsights}
                        onAdd={handleAddInsight}
                        onUpdate={handleUpdateInsight}
                        onDelete={handleDeleteInsight}
                    />
                </Box>
            )}
        </Box>
    );
}

export default HappySignalOverview;